import {
    AGE_BRACKET_LABEL_KEYS,
    DEMOGRAPHIC_AGE_BRACKETS,
    DEMOGRAPHIC_GENDERS,
    DemographicAgeBracket,
    DemographicGender,
    GENDER_LABEL_KEYS,
} from '@app/constants/demographics';
import { buildInsightsCsvFilterRows } from './buildInsightsCsvFilterRows';

type Translate = (
    key: string,
    options?: { ns?: string; defaultValue?: string },
) => string;

export type BuildBroadcastDemographicsCsvFilterRowsParams = {
    filters?: any;
    t: Translate;
};

const pushRow = (rows: string[][], label: string, value: string | null) => {
    const v = (value ?? '').trim();
    if (!v) return;
    rows.push([label, v]);
};

const formatCodeSelection = (
    selected: string[] | undefined,
    allCodes: readonly string[],
    labelOf: (code: string) => string,
    allLabel: string,
): string | null => {
    if (!selected?.length) {
        return null;
    }
    if (selected.length >= allCodes.length) {
        return allLabel;
    }
    const parts = selected.map(labelOf).filter(Boolean);
    return parts.join('; ') || null;
};

// Shared Broadcast Exposure filters (dates, placements, networks, …) plus
// the tab's Age Bracket / Gender selection. Empty demographic selections
// mean "every value" and are omitted, matching the other list filters.
export const buildBroadcastDemographicsCsvFilterRows = ({
    filters,
    t,
}: BuildBroadcastDemographicsCsvFilterRowsParams): string[][] => {
    const primaryDateLabel = t('dataExplorer.csvExport.primaryDateRange', {
        ns: 'measurement',
    });
    const dateRangeLabel = t('dataExplorer.csvExport.dateRange', {
        ns: 'measurement',
    });
    const broadcastOptionLabel = t('filters.broadcastName', { ns: 'common' });
    const rows = buildInsightsCsvFilterRows({
        filters,
        query: {},
        hasCompareDates: false,
        t,
    })
        .map(([label, value]) =>
            label === primaryDateLabel
                ? [dateRangeLabel, value]
                : [label, value],
        )
        // `broadcastOptions` is the selected index list (`0; 1`), not names.
        .filter(([label]) => label !== broadcastOptionLabel);
    const allLabel = t('dataExplorer.csvExport.allSelected', {
        ns: 'measurement',
    });

    pushRow(
        rows,
        t('filters.ageBracket', { ns: 'common' }),
        formatCodeSelection(
            filters?.ageBrackets as DemographicAgeBracket[] | undefined,
            DEMOGRAPHIC_AGE_BRACKETS,
            code => t(AGE_BRACKET_LABEL_KEYS[code], { ns: 'common' }),
            allLabel,
        ),
    );
    pushRow(
        rows,
        t('filters.gender', { ns: 'common' }),
        formatCodeSelection(
            filters?.genders as DemographicGender[] | undefined,
            DEMOGRAPHIC_GENDERS,
            code => t(GENDER_LABEL_KEYS[code], { ns: 'common' }),
            allLabel,
        ),
    );

    return rows;
};
